export const formatCurrency = (amount, currency = 'USD', locale = 'en-US') => {
  const value = Number(amount || 0);
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  } catch {
    // Fallback
    const v = isFinite(value) ? value : 0;
    return `${currency === 'USD' ? '$' : ''}${v.toFixed(2)}`;
  }
};

/**
 * Build a display name from a person/entity record.
 * Handles the common patterns: name, full_name, first+last, company_name.
 * @param {Object} record - Any object with name fields (employee, customer, user, etc.)
 * @param {string} fallback - What to show if no name found (default: 'Unknown')
 * @returns {string}
 */
export const getDisplayName = (record, fallback = 'Unknown') => {
  if (!record) return fallback;
  if (record.full_name) return record.full_name;
  if (record.name) return record.name;
  if (record.company_name) return record.company_name;
  const first = (record.first_name || '').trim();
  const last = (record.last_name || '').trim();
  const combined = `${first} ${last}`.trim();
  return combined || fallback;
};

export const formatHours = (hours) => {
  const h = Number(hours || 0);
  if (!isFinite(h)) return '0 hrs';
  if (h === 0) return '0 hrs';
  if (h < 8) return `${h.toFixed(1)} hrs`;
  const days = Math.floor(h / 8);
  const rem = h % 8;
  return rem > 0 ? `${days}d ${rem.toFixed(1)}h` : `${days}d`;
};


/**
 * US ZIP / ZIP+4, as one rule for every surface that asks for one.
 *
 * Board #407: Company Profile's input ran `value.replace(/\D/g, '').slice(0, 10)`, so typing
 * the standard `97058-4334` stored and displayed `970584334` — nine digits run together —
 * while onboarding accepted the same value and showed the hyphen. Two surfaces for one field,
 * disagreeing about what the field looks like. The hyphen is the USPS form and is what the
 * user typed, so it is what we keep.
 *
 * Live 2026-09-17: of 291 companies, 126 hold a plain 5-digit code, 1 is already hyphenated
 * and 1 is nine bare digits — so the bare-nine legacy shape has to keep validating, and it
 * normalises to the hyphenated form the next time the row is saved.
 *
 * Digits-only by design: this is a US ZIP field. A Canadian postcode (A1A 1A1) does NOT
 * survive it, which is a real gap — onboarding offers Canada as a business country — but it
 * is a separate finding, not this one, and the behaviour here is no worse than before.
 */
export const formatPostalCode = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

/**
 * Accepts `12345`, `12345-6789` and the legacy unseparated `123456789`. The old rule was
 * /^\d{5}(\d{4})?$/, which REJECTED the hyphenated form — so formatting the input without
 * fixing this would have made a correctly-typed ZIP+4 unsaveable.
 */
export const isValidPostalCode = (value) => /^\d{5}(-?\d{4})?$/.test(String(value || '').trim());

/**
 * Is this a web address we can safely publish as the company's site?
 *
 * Board #464: Company Profile only ever PREFIXED the value with `https://` before storing it,
 * so "my shop" became "https://my shop" and went onto the public profile as a dead link.
 *
 * Deliberately NOT a .com check. A contractor may be on .co.uk, .plumbing or any national
 * domain, and rejecting those is the more damaging error. The rule is only that the address
 * parses, speaks http(s), and names a real PUBLIC host — so a hostname with no dot
 * (`localhost`, a bare `plumbing`) and a raw IP address are refused, and everything a real
 * business would type is accepted. Verified against the URL parser 2026-09-17: a space makes
 * `new URL` throw, while `localhost` and `plumbing` parse and are caught by the dot rule.
 *
 * Accepts a bare domain — the caller is expected to normalise the scheme on the way in.
 *
 * NOTE: `CompanyProfile/WebsitePreviewCard.js` and `CompanyProfile/SocialLinksCard.js` still
 * carry their own looser `new URL` checks. They are not repointed here because #464 covers the
 * save path only; this is the owner to extend when they are.
 */
export const isPublicWebsiteUrl = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return false;
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  let host;
  try {
    const url = new URL(withScheme);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
    host = url.hostname;
  } catch {
    return false;
  }
  if (!host.includes('.')) return false;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return false;
  // A trailing or doubled dot parses but is not a hostname anyone owns.
  return !host.startsWith('.') && !host.endsWith('.') && !host.includes('..');
};
